import React, { useState } from 'react';
import {
  Card, Table, Button, Space, Typography, Form, Input,
  Drawer, DatePicker, Popconfirm, message, Tabs, Empty, Tag, Checkbox
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, PlusOutlined,
  EditOutlined, DeleteOutlined, EyeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

// TODO: 联调时替换为接口数据
const MOCK_DATA = [
  { key: '1', title: 'test-设备分组管理上线', publishTime: '2026-07-01 10:00:00', content: '## 新功能\n\n- 支持按产品/区域/标签灵活分组\n- 分组后可批量执行固件升级和配置下发\n\n> 本次更新预计在 7 月 5 日前全量推送', visibleTo: ['enterprise', 'platform'], createdAt: '2026-07-01 09:50:00', updatedAt: '2026-07-01 09:50:00' },
  { key: '2', title: 'test-消费账户余额监控报警优化', publishTime: '2026-06-28 14:30:00', content: '## 优化\n\n- 报警阈值支持自定义\n- 新增短信提醒频率控制\n- 修复部分企业未收到提醒的问题', visibleTo: ['enterprise'], createdAt: '2026-06-28 14:00:00', updatedAt: '2026-06-29 10:00:00' },
  { key: '3', title: 'test-信用额度审批流程优化', publishTime: '2026-06-25 09:00:00', content: '## 平台内部更新\n\n- 信用额度审批支持批量操作\n- 审批流程节点简化', visibleTo: ['platform'], createdAt: '2026-06-25 08:30:00', updatedAt: '2026-06-25 08:30:00' },
  { key: '4', title: 'test-套餐底价扣除配置上线', publishTime: '2026-06-20 09:00:00', content: '## 新功能\n\n### 企业商务配置\n平台客户企业详情新增**套餐底价扣除配置**：\n1. 支持设置扣除打折比例\n2. 支持按 App 应用组单独配置', visibleTo: ['enterprise'], createdAt: '2026-06-20 08:30:00', updatedAt: '2026-06-20 08:30:00' },
  { key: '5', title: 'test-佣金抽成规则调整', publishTime: '2026-06-15 16:00:00', content: '## 平台内部更新\n\n- 抽佣比例上限调整\n- 新增阶梯抽佣模式', visibleTo: ['platform'], createdAt: '2026-06-15 15:00:00', updatedAt: '2026-06-15 15:00:00' },
  { key: '6', title: 'test-固件 OTA 推送优化', publishTime: '2026-06-10 11:00:00', content: '## 优化\n\n- 大文件分片推送\n- 推送进度实时展示\n- 失败自动重试 3 次', visibleTo: ['enterprise', 'platform'], createdAt: '2026-06-10 10:00:00', updatedAt: '2026-06-12 09:00:00' },
];

const AUDIENCE_OPTIONS = [
  { label: '平台用户', value: 'platform' },
  { label: '企业用户', value: 'enterprise' },
];

const AUDIENCE_TAGS = {
  platform: { label: '平台用户', color: 'blue' },
  enterprise: { label: '企业用户', color: 'green' },
};

// 简易 Markdown → HTML 转换
function simpleMarkdownToHtml(md) {
  if (!md) return '';
  let html = md
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<h4 style="margin:12px 0 8px">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 style="margin:16px 0 8px">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 style="margin:20px 0 10px">$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code style="background:#f5f5f5;padding:2px 6px;border-radius:3px">$1</code>')
    .replace(/^> (.+)$/gm, '<blockquote style="border-left:3px solid #ddd;margin:8px 0;padding:4px 12px;color:#666">$1</blockquote>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/^(\d+)\. (.+)$/gm, '<li>$2</li>')
    .replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul style="margin:4px 0;padding-left:20px">$1</ul>')
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" style="color:#1677ff">$1</a>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
  return html;
}

const ReleaseNotes = () => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [dataSource, setDataSource] = useState(MOCK_DATA);
  const [filteredData, setFilteredData] = useState(MOCK_DATA);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewRecord, setPreviewRecord] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [contentMode, setContentMode] = useState('edit');

  const handleSearch = () => {
    const { title } = form.getFieldsValue();
    if (!title || !title.trim()) { setFilteredData(dataSource); return; }
    const kw = title.trim().toLowerCase();
    setFilteredData(dataSource.filter(r => r.title.toLowerCase().includes(kw)));
  };

  const handleReset = () => { form.resetFields(); setFilteredData(dataSource); };

  const openPreview = (record) => { setPreviewRecord(record); setPreviewVisible(true); };

  const openCreate = () => {
    setEditingRecord(null);
    editForm.resetFields();
    editForm.setFieldsValue({ publishTime: dayjs(), visibleTo: ['enterprise'] });
    setContentMode('edit');
    setDrawerVisible(true);
  };

  const openEdit = (record) => {
    setEditingRecord(record);
    editForm.setFieldsValue({
      title: record.title,
      publishTime: dayjs(record.publishTime),
      content: record.content,
      visibleTo: record.visibleTo || ['enterprise'],
    });
    setContentMode('edit');
    setDrawerVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await editForm.validateFields();
      if (!values.visibleTo || values.visibleTo.length === 0) {
        message.warning('请至少选择一项可见受众');
        return;
      }
      const pubTime = values.publishTime.format('YYYY-MM-DD HH:mm:ss');
      const now = dayjs().format('YYYY-MM-DD HH:mm:ss');
      let record = null;
      if (editingRecord) {
        record = { ...editingRecord, title: values.title, publishTime: pubTime, content: values.content, visibleTo: values.visibleTo, updatedAt: now };
        setDataSource(prev => prev.map(r => r.key === editingRecord.key ? record : r));
        message.success('发版公告已更新');
      } else {
        record = {
          key: String(Date.now()),
          title: values.title,
          publishTime: pubTime,
          content: values.content,
          visibleTo: values.visibleTo,
          createdAt: now,
          updatedAt: now,
        };
        setDataSource(prev => [record, ...prev]);
        message.success('发版公告已发布');
      }
      setFilteredData(prev => editingRecord
        ? prev.map(r => r.key === editingRecord.key ? record : r)
        : [record, ...prev]
      );
      setDrawerVisible(false);
      // 新公告发布后，清空对应受众的已读记录（模拟后端行为）
      const existing = JSON.parse(localStorage.getItem('announcement_read') || '{}');
      Object.keys(existing).forEach(key => {
        if (key.endsWith('_' + record.key)) delete existing[key];
      });
      localStorage.setItem('announcement_read', JSON.stringify(existing));
    } catch (e) { /* validation */ }
  };

  const handleDelete = (record) => {
    setDataSource(prev => prev.filter(r => r.key !== record.key));
    setFilteredData(prev => prev.filter(r => r.key !== record.key));
    // 清理已读记录
    const existing = JSON.parse(localStorage.getItem('announcement_read') || '{}');
    Object.keys(existing).forEach(key => {
      if (key.endsWith('_' + record.key)) delete existing[key];
    });
    localStorage.setItem('announcement_read', JSON.stringify(existing));
    message.success('已删除');
  };

  const columns = [
    { title: '标题', dataIndex: 'title', key: 'title', ellipsis: true },
    { title: '可见受众', dataIndex: 'visibleTo', key: 'visibleTo', width: 150, render: (v) => (
      <Space size={4}>
        {(v || ['enterprise']).map(t => <Tag key={t} color={AUDIENCE_TAGS[t]?.color || 'default'}>{AUDIENCE_TAGS[t]?.label || t}</Tag>)}
      </Space>
    )},
    { title: '发布时间', dataIndex: 'publishTime', key: 'publishTime', width: 170, render: (t) => <Text type="secondary">{t}</Text> },
    { title: '发布内容', key: 'preview', width: 100, render: (_, r) => (
      <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openPreview(r)}>预览</Button>
    )},
    { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 170, render: (t) => <Text type="secondary">{t}</Text> },
    { title: '更新时间', dataIndex: 'updatedAt', key: 'updatedAt', width: 170, render: (t) => <Text type="secondary">{t}</Text> },
    {
      title: '操作', key: 'action', width: 140, fixed: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm title="确认删除该发版公告？" onConfirm={() => handleDelete(r)} okText="确认" cancelText="取消">
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>发版公告</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新建</Button>
      </div>

      <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Form form={form} layout="inline">
          <Form.Item name="title" style={{ marginBottom: 0 }}>
            <Input placeholder="搜索标题" allowClear style={{ width: 240 }} onPressEnter={handleSearch} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card bordered={false}>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="key"
          bordered={false}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
          locale={{ emptyText: <Empty description="暂无发版公告" /> }}
          scroll={{ x: 1100 }}
        />
      </Card>

      {/* 内容预览抽屉 */}
      <Drawer
        title={previewRecord ? `预览 - ${previewRecord.title}` : '预览'}
        open={previewVisible}
        onClose={() => setPreviewVisible(false)}
        width={600}
        destroyOnClose
      >
        {previewRecord && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Space size={4}>
                {(previewRecord.visibleTo || ['enterprise']).map(t => (
                  <Tag key={t} color={AUDIENCE_TAGS[t]?.color || 'default'}>{AUDIENCE_TAGS[t]?.label || t}</Tag>
                ))}
              </Space>
            </div>
            <div
              style={{ fontSize: 14, lineHeight: 1.8 }}
              dangerouslySetInnerHTML={{ __html: simpleMarkdownToHtml(previewRecord.content) }}
            />
          </div>
        )}
      </Drawer>

      {/* 新建/编辑抽屉 */}
      <Drawer
        title={editingRecord ? '编辑发版公告' : '新建发版公告'}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={720}
        destroyOnClose
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setDrawerVisible(false)}>取消</Button>
              <Button type="primary" onClick={handleSave}>保存</Button>
            </Space>
          </div>
        }
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="请输入发版公告标题" />
          </Form.Item>

          <Form.Item name="publishTime" label="发布时间" rules={[{ required: true, message: '请选择发布时间' }]}>
            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="content"
            label="发布内容"
            rules={[{ required: true, message: '请输入发布内容' }]}
            extra="支持 Markdown 语法：标题、列表、引用、链接等"
          >
            <Tabs
              activeKey={contentMode}
              onChange={setContentMode}
              size="small"
              items={[
                {
                  key: 'edit',
                  label: '编辑',
                  children: (
                    <TextArea
                      id="release-content-editor"
                      rows={16}
                      placeholder={`## 新功能\n\n- 功能点 1\n- 功能点 2\n\n> 引用说明`}
                      style={{ fontFamily: 'monospace', fontSize: 13 }}
                    />
                  ),
                },
                {
                  key: 'preview',
                  label: '预览',
                  children: (
                    <div
                      style={{
                        minHeight: 300, padding: '12px 16px', background: '#fafafa',
                        borderRadius: 8, border: '1px solid #f0f0f0',
                        fontSize: 14, lineHeight: 1.8,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: simpleMarkdownToHtml(editForm.getFieldValue('content') || ''),
                      }}
                    />
                  ),
                },
              ]}
            />
          </Form.Item>

          <div style={{ background: '#f9f9f9', padding: 16, borderRadius: 8 }}>
            <Form.Item
              name="visibleTo"
              label="可见受众"
              rules={[{ required: true, message: '请至少选择一项可见受众' }]}
              extra="至少选择一项"
            >
              <Checkbox.Group options={AUDIENCE_OPTIONS} />
            </Form.Item>
          </div>
        </Form>
      </Drawer>
    </div>
  );
};

export default ReleaseNotes;
